import { isClickedOutside } from '@theme/utilities';

const AUTO_CLOSE_DELAY_MS = 3000;

class BackInStockForm extends HTMLElement {
  #boundHandleSubmit = this.handleSubmit.bind(this);
  #boundHandleKlaviyoBisFormSubmitted = this.handleKlaviyoBisFormSubmitted.bind(this);
  #boundHandleDialogClose = this.#resetFormState.bind(this);
  #boundHandleManualCloseStart = this.#handleManualCloseStart.bind(this);
  #boundHandleDialogComponentClick = this.#handleDialogComponentClick.bind(this);
  #boundHandleDialogComponentKeydown = this.#handleDialogComponentKeydown.bind(this);
  /** @type {ReturnType<typeof setTimeout> | null} */
  #autoCloseTimeoutId = null;
  /** @type {HTMLElement | null} */
  #dialogComponent = null;
  /** @type {HTMLButtonElement | null} */
  #closeButton = null;

  constructor() {
    super();
    this.form = this.querySelector('form');
  }

  connectedCallback() {
    this.form?.addEventListener('submit', this.#boundHandleSubmit);
    this.addEventListener('klaviyo-bis-form-submitted', this.#boundHandleKlaviyoBisFormSubmitted);

    this.#dialogComponent = this.closest('dialog-component');
    this.#dialogComponent?.addEventListener('dialog:close', this.#boundHandleDialogClose);
    this.#dialogComponent?.addEventListener('click', this.#boundHandleDialogComponentClick, true);
    this.#dialogComponent?.addEventListener('keydown', this.#boundHandleDialogComponentKeydown, true);

    this.#closeButton = this.#dialogComponent?.querySelector('.klaviyo-bis-dialog-close-button') ?? null;
    this.#closeButton?.addEventListener('click', this.#boundHandleManualCloseStart);
  }

  #handleManualCloseStart() {
    this.#clearAutoCloseTimeout();
  }

  // @ts-ignore
  #handleDialogComponentClick(event) {
    if (!this.hasAttribute('data-submitted')) return;

    const dialog = this.#dialogComponent?.querySelector('dialog');
    if (!dialog?.open) return;

    if (isClickedOutside(event, dialog)) {
      this.#clearAutoCloseTimeout();
    }
  }

  // @ts-ignore
  #handleDialogComponentKeydown(event) {
    if (event.key !== 'Escape' || !this.hasAttribute('data-submitted')) return;

    this.#clearAutoCloseTimeout();
  }

  /**
   * @param {Element | null | undefined} dialog
   */
  #canAutoCloseDialog(dialog) {
    return dialog instanceof HTMLDialogElement
      && dialog.open
      && !dialog.classList.contains('dialog-closing');
  }

  #showSuccessState() {
    this.#clearAutoCloseTimeout();

    const emailInput = this.querySelector('#email');
    if (emailInput instanceof HTMLInputElement) {
      emailInput.disabled = true;
    }

    const newsletterCheckbox = this.querySelector('#subscribe_for_newsletter');
    if (newsletterCheckbox instanceof HTMLInputElement) {
      newsletterCheckbox.disabled = true;
    }
    const formGroup = this.querySelector('.klaviyo-bis-dialog-form-group');
    formGroup?.classList.add('hidden');

    const submitButton = this.querySelector('.klaviyo-bis-dialog-submit-button');
    submitButton?.classList.add('hidden');

    const completedMessage = this.querySelector('.klaviyo-bis-dialog-form-completed-message');
    if (completedMessage instanceof HTMLElement) {
      completedMessage.classList.remove('hidden');
      requestAnimationFrame(() => {
        completedMessage.focus();
      });
    }

    this.setAttribute('data-submitted', 'true');

    this.#autoCloseTimeoutId = setTimeout(() => {
      this.#autoCloseTimeoutId = null;
      const dialogComponent = this.closest('dialog-component');
      const dialog = dialogComponent?.querySelector('dialog');

      if (!this.#canAutoCloseDialog(dialog)) return;

      if (dialogComponent && 'closeDialog' in dialogComponent && typeof dialogComponent.closeDialog === 'function') {
        dialogComponent.closeDialog();
      }
    }, AUTO_CLOSE_DELAY_MS);
  }

  // @ts-ignore
  handleKlaviyoBisFormSubmitted(event) {
    event.preventDefault();
    event.stopPropagation();
    this.#showSuccessState();
  }

  async handleSubmit(/** @type {Event} */ event) {
    event.preventDefault();

    const form = /** @type {HTMLFormElement} */ (event.target);
    const bisForm = form.closest('back-in-stock-form');
    if (!bisForm) return;

    const errorEl = bisForm.querySelector('.klaviyo-bis-dialog-form-error');
    errorEl?.classList.add('hidden');

    const successBackInStock = await this.#submitToKlaviyo(form.elements);
    if (!successBackInStock) {
      errorEl?.classList.remove('hidden');
      return;
    }

    const subscribeForNewsletter = /** @type {any} */ (form.elements).subscribe_for_newsletter.checked;
    if (subscribeForNewsletter) {
        const successNewsletter = await this.#subscribeToNewsletter(form.elements);
      if (!successNewsletter) {
        console.error('Failed to subscribe to newsletter');
      }
    }

    this.dispatchEvent(new CustomEvent('klaviyo-bis-form-submitted', {
      bubbles: true,
      composed: true,
    }));
  }

  #resetFormState() {
    this.#clearAutoCloseTimeout();
    this.removeAttribute('data-submitted');

    const emailInput = this.querySelector('#email');
    if (emailInput instanceof HTMLInputElement) {
      emailInput.disabled = false;
    }

    const newsletterCheckbox = this.querySelector('#subscribe_for_newsletter');
    if (newsletterCheckbox instanceof HTMLInputElement) {
      newsletterCheckbox.disabled = false;
    }

    const formGroup = this.querySelector('.klaviyo-bis-dialog-form-group');
    formGroup?.classList.remove('hidden');

    const submitButton = this.querySelector('.klaviyo-bis-dialog-submit-button');
    submitButton?.classList.remove('hidden');

    const completedMessage = this.querySelector('.klaviyo-bis-dialog-form-completed-message');
    completedMessage?.classList.add('hidden');

    const errorEl = this.querySelector('.klaviyo-bis-dialog-form-error');
    errorEl?.classList.add('hidden');

    this.form?.reset();
  }

  #clearAutoCloseTimeout() {
    if (this.#autoCloseTimeoutId != null) {
      clearTimeout(this.#autoCloseTimeoutId);
      this.#autoCloseTimeoutId = null;
    }
  }

  // @ts-ignore
  async #subscribeToNewsletter(elements) {
    const email = elements.email.value;
    const listId = elements.g.value;
    const klaviyoAccountId = elements.a.value;
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: {
                  $source: 'Back in Stock',
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };

    const options = {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        revision: '2026-04-15',
        'content-type': 'application/vnd.api+json'
      },
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + klaviyoAccountId, options);
      if (!response.ok) {
        console.error('Failed to subscribe to newsletter', response.status);
      }
      return response.ok;
    } catch (error) {
      console.error('Failed to subscribe to newsletter', error);
      return false;
    }
  }

  async #submitToKlaviyo(/** @type {any} */ elements) {
    const email = elements.email.value;
    const klaviyoAccountId = elements.a.value;
    const variant = elements.variant.value;

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
              },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variant}`,
            },
          },
        },
      },
    };
    const options = {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        revision: '2026-04-15',
        'content-type': 'application/vnd.api+json'
      },
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=' + klaviyoAccountId, options);
      if (!response.ok) {
        console.error('Failed to submit back in stock subscription to Klaviyo', response.status);
      }
      return response.ok;
    } catch (error) {
      console.error('Failed to submit back in stock subscription to Klaviyo', error);
      return false;
    }
  }

  disconnectedCallback() {
    this.#clearAutoCloseTimeout();
    this.form?.removeEventListener('submit', this.#boundHandleSubmit);
    this.removeEventListener('klaviyo-bis-form-submitted', this.#boundHandleKlaviyoBisFormSubmitted);
    this.#dialogComponent?.removeEventListener('dialog:close', this.#boundHandleDialogClose);
    this.#dialogComponent?.removeEventListener('click', this.#boundHandleDialogComponentClick, true);
    this.#dialogComponent?.removeEventListener('keydown', this.#boundHandleDialogComponentKeydown, true);
    this.#closeButton?.removeEventListener('click', this.#boundHandleManualCloseStart);
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.classList.contains('klaviyo-bis-dialog-form')) return;

  const bisForm = form.closest('back-in-stock-form');
  if (!(bisForm instanceof BackInStockForm)) return;

  bisForm.handleSubmit(event);
}, { capture: true });
